import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Cpu, Landmark, RefreshCw, Activity, Hash } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, fmtDate } from "@/lib/api";
import { useMe } from "@/context/MeContext";
import { Button } from "@/components/ui/button";

const VERDICT = {
  SUPPORTED: ["Supported by approved sources", "bg-emerald-50 text-emerald-800 border-emerald-200"],
  MIXED: ["Approved sources disagree", "bg-amber-50 text-amber-800 border-amber-300"],
  "INSUFFICIENT EVIDENCE": ["Not yet confirmed by approved sources", "bg-stone-100 text-stone-700 border-stone-300"],
};

const FactLinks = ({ facts, testid }) => facts?.length ? (
  <p className="mt-1.5 flex flex-wrap gap-x-3 gap-y-1 text-[11px]" data-testid={testid}>
    {facts.map((f) => <Link key={f.id} to={`/facts/${f.id}`} className="text-[hsl(var(--facts))] hover:underline">{f.source_name || "Fact"}: {f.headline}</Link>)}
  </p>
) : null;

const Panel = ({ icon: Icon, title, note, children, testid, wide }) => (
  <div className={`rounded-2xl border border-border bg-white p-4 ${wide ? "lg:col-span-2" : ""}`} data-testid={testid}>
    <p className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-slate-500"><Icon size={13} />{title}</p>
    {note && <p className="mt-0.5 text-[11px] text-slate-500">{note}</p>}
    <div className="mt-3">{children}</div>
  </div>
);

const Tension = ({ t }) => (
  <Panel icon={Activity} title="Tension at a glance" note="The latest military, trade and diplomatic moves, from verified Facts only" testid="brief-tension" wide>
    {t?.text ? <><p className="font-serif text-lg leading-relaxed text-slate-800" data-testid="brief-tension-text">{t.text}</p><FactLinks facts={t.facts} testid="brief-tension-facts" /></>
      : <p className="text-sm text-slate-500">No verified updates yet. This fills in as approved sources report new developments.</p>}
  </Panel>
);

const Makers = ({ rows }) => (
  <Panel icon={Cpu} title="Where advanced chips are made" note="From companies' public records and verified Facts" testid="brief-chip-makers">
    {rows?.length ? <ul className="divide-y divide-border">{rows.map((m, i) => (
      <li key={`${m.place}-${i}`} className="py-2.5 first:pt-0 last:pb-0" data-testid={`brief-maker-${i}`}>
        <div className="flex flex-wrap items-baseline gap-2"><b className="text-sm">{m.place}</b><span className="text-xs text-slate-500">{m.companies.join(", ")}</span>
          <span className={`ml-auto rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase ${m.status === "operating" ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-700"}`}>{m.status}</span></div>
        <p className="mt-0.5 text-sm text-slate-700">{m.makes}</p>
        <FactLinks facts={m.facts} testid={`brief-maker-facts-${i}`} />
      </li>))}</ul> : <p className="text-sm text-slate-500">Not available yet.</p>}
  </Panel>
);

const Numbers = ({ rows }) => (
  <Panel icon={Hash} title="Key numbers" note="Every number comes from a verified Fact" testid="brief-key-numbers">
    {rows?.length ? <ul className="space-y-3">{rows.map((n, i) => (
      <li key={i} data-testid={`brief-number-${i}`}>
        <p className="font-serif text-3xl leading-none">{n.value}</p>
        <p className="mt-1 text-sm text-slate-700">{n.label}</p>
        <FactLinks facts={[n.fact]} testid={`brief-number-fact-${i}`} />
      </li>))}</ul> : <p className="text-sm text-slate-500">No numbers have been reported in verified Facts yet.</p>}
  </Panel>
);

const Positions = ({ rows }) => (
  <Panel icon={Landmark} title="Formal policy positions" note="Official positions from laws, treaties and government statements. Each one is fact-checked against Middle's approved sources" testid="brief-positions" wide>
    {rows?.length ? <ul className="grid gap-3 sm:grid-cols-2">{rows.map((x, i) => {
      const [label, cls] = VERDICT[x.check?.verdict] || VERDICT["INSUFFICIENT EVIDENCE"];
      return (
        <li key={`${x.country}-${i}`} className="rounded-xl border border-border bg-[#FAF8F5] p-3" data-testid={`brief-position-${i}`}>
          <p className="text-sm font-semibold">{x.country}</p>
          <p className="mt-1 text-sm text-slate-700">{x.position}</p>
          {x.basis && <p className="mt-1 text-[11px] text-slate-500">Basis: {x.basis}</p>}
          <span className={`mt-2 inline-block rounded-full border px-2 py-0.5 text-[11px] font-medium ${cls}`} title={x.check?.explanation} data-testid={`brief-position-check-${i}`}>{label}</span>
          {(x.check?.sources || []).slice(0, 2).map((s) => <a key={s.id} href={s.url || undefined} target="_blank" rel="noreferrer" className="mt-1 block text-[11px] text-[hsl(var(--facts))] hover:underline">{s.source}: {s.title}</a>)}
          <FactLinks facts={x.facts} testid={`brief-position-facts-${i}`} />
        </li>);
    })}</ul> : <p className="text-sm text-slate-500">Not available yet.</p>}
  </Panel>
);

export const IssueBrief = ({ liveId }) => {
  const { me } = useMe();
  const [b, setB] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get(`/live/${liveId}/brief`).then((r) => setB(r.data)).catch(() => setB({})); }, [liveId]);
  const refresh = async () => { setBusy(true); try { setB((await api.post(`/admin/live/${liveId}/brief/refresh`)).data); toast.success("Updated from the latest verified Facts"); } catch (e) { toast.error(errMsg(e)); } setBusy(false); };
  if (!b) return null;
  return (
    <section className="surface max-w-5xl space-y-4 p-5 sm:p-6" data-testid="issue-brief">
      <div className="flex flex-wrap items-center gap-2">
        <p className="flex-1 text-xs font-semibold uppercase tracking-wider text-[hsl(var(--facts))]">At a glance</p>
        <span className="text-[11px] text-slate-500" data-testid="issue-brief-origin">AI-drafted from verified Facts, updated every few hours{b.generated_at && ` · ${fmtDate(b.generated_at)}`}</span>
        {me?.role === "admin" && <Button size="sm" variant="ghost" className="rounded-full" disabled={busy} onClick={refresh} data-testid="issue-brief-refresh-btn"><RefreshCw size={14} className={`mr-1 ${busy ? "animate-spin" : ""}`} />Refresh with AI</Button>}
      </div>
      <div className="grid items-start gap-3 lg:grid-cols-2">
        <Tension t={b.tension} />
        <Makers rows={b.chip_makers} />
        <Numbers rows={b.key_numbers} />
        <Positions rows={b.positions} />
      </div>
      <p className="text-[11px] text-slate-400">Middle reports only what has happened. It doesn't predict or describe possible conflicts.</p>
    </section>
  );
};
